import { generateClient } from 'aws-amplify/api';
import * as streamRouterMutations from '@/graphql/streamRouterMutations';
import * as qlmutations from '@/graphql/mutations';
import * as qlqueries from '@/graphql/queries';
import * as routerQueries from '@/graphql/routerQueries';
import * as routerMutations from '@/graphql/routerMutations';

const client = generateClient();
const graphqlOperation = (query, variables) => ({ query, variables });

function parseCommandDataJson(dataJson, fallbackValue) {
  if (!dataJson) return fallbackValue;
  try {
    return JSON.parse(dataJson);
  } catch (error) {
    console.error('Failed to parse command data JSON:', error);
    return fallbackValue;
  }
}

const state = {
  // Stream Router
  streamRouters: [],
  routerInterfaces: [],

  // Wowza
  wowzaServers: [],
  wowzaApplications: [],
  wowzaTargets: [],
  transcoderTemplates: [],

  // MediaConnect
  mediaConnectNetworkInterfaces: [],
  mediaConnectOutputs: [],
  mediaConnectLoading: false,
  mediaConnectError: null,

  // Equipment
  equipment: [],

  // Loading states
  loading: false,

  // Error handling
  lastError: null,
};

const getters = {
  streamRouters: (state) => state.streamRouters,
  routerInterfaces: (state) => state.routerInterfaces,
  wowzaServers: (state) => state.wowzaServers.filter(eq => eq.equipmentType === 'WOWZA'),
  wowzaApplications: (state) => state.wowzaApplications,
  wowzaTargets: (state) => state.wowzaTargets,
  transcoderTemplates: (state) => state.transcoderTemplates,
  mediaConnectNetworkInterfaces: (state) => state.mediaConnectNetworkInterfaces,
  mediaConnectOutputs: (state) => state.mediaConnectOutputs,
  mediaConnectLoading: (state) => state.mediaConnectLoading,
  mediaConnectError: (state) => state.mediaConnectError,
  mediaConnectOutputById: (state) => (outputId) => (
    (state.mediaConnectOutputs || []).find((output) => output?.Id === outputId) || null
  ),
  equipment: (state) => state.equipment,
  isLoading: (state) => state.loading,
  lastError: (state) => state.lastError,
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },

  setError(state, error) {
    state.lastError = error;
  },

  clearError(state) {
    state.lastError = null;
  },

  setStreamRouters(state, routers) {
    state.streamRouters = routers;
  },

  setRouterInterfaces(state, interfaces) {
    state.routerInterfaces = interfaces;
  },

  addRouterInterface(state, routerInterface) {
    state.routerInterfaces.push(routerInterface);
  },

  removeRouterInterface(state, interfaceId) {
    state.routerInterfaces = state.routerInterfaces.filter(
      iface => iface.interfaceId !== interfaceId
    );
  },

  setWowzaServers(state, servers) {
    state.wowzaServers = servers;
  },

  setWowzaApplications(state, applications) {
    state.wowzaApplications = applications;
  },

  setWowzaTargets(state, targets) {
    state.wowzaTargets = targets;
  },

  addWowzaTarget(state, target) {
    state.wowzaTargets.push(target);
  },

  setTranscoderTemplates(state, templates) {
    state.transcoderTemplates = templates;
  },

  setEquipment(state, equipment) {
    state.equipment = equipment;
  },

  setMediaConnectLoading(state, loading) {
    state.mediaConnectLoading = loading;
  },

  setMediaConnectError(state, error) {
    state.mediaConnectError = error;
  },

  clearMediaConnectError(state) {
    state.mediaConnectError = null;
  },

  setMediaConnectNetworkInterfaces(state, interfaces) {
    state.mediaConnectNetworkInterfaces = interfaces;
  },

  setMediaConnectOutputs(state, outputs) {
    state.mediaConnectOutputs = outputs;
  },
};

const actions = {
  // Stream Router Actions
  async loadStreamRouters({ commit }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      const result = await client.graphql(
        graphqlOperation(qlqueries.listRouters, {
          limit: 1000
        })
      );

      const routers = result.data.listRouters.items || [];
      commit('setStreamRouters', routers);

      return { success: true, data: routers };
    } catch (error) {
      console.error('Failed to load stream routers:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to load stream routers' });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async createStreamRouterInterface({ commit }, { streamRouterId, interfaceData }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      // Validate required destination.url
      if (!interfaceData.destination?.url) {
        throw new Error('destination.url is required for Stream Router Interface');
      }

      console.log('Creating Stream Router Interface:', {
        streamRouterId,
        interfaceData: {
          ...interfaceData,
          destination: {
            ...interfaceData.destination,
            url: interfaceData.destination.url // Ensure URL is populated
          }
        }
      });

      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.createStreamRouterInterface, {
          streamRouterId,
          interfaceData
        })
      );

      const response = result.data.createStreamRouterInterface;

      if (!response.success) {
        throw new Error(response.message || 'Failed to create interface');
      }

      commit('addRouterInterface', {
        streamRouterId,
        ...response.data
      });

      this._vm?.$message?.success({ message: 'Stream Router Interface created successfully' });

      return {
        success: true,
        data: response.data,
        smEndpoints: [response.data.source, response.data.destination],
        config: {
          interfaceId: response.data.interfaceId,
          interfaceName: response.data.name,
          streamManagerId: response.data.streamManagerId,
          interfaceDescription: response.data.description || '',
          type: 'SR'
        }
      };
    } catch (error) {
      console.error('Failed to create Stream Router Interface:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to create Stream Router Interface: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async deleteStreamRouterInterface({ commit }, { streamRouterId, interfaceId }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.deleteStreamRouterInterface, {
          streamRouterId,
          interfaceId
        })
      );

      const response = result.data.deleteStreamRouterInterface;

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete interface');
      }

      commit('removeRouterInterface', interfaceId);
      this._vm?.$message?.success({ message: 'Stream Router Interface deleted successfully' });

      return { success: true };
    } catch (error) {
      console.error('Failed to delete Stream Router Interface:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to delete Stream Router Interface' });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  // Wowza Actions
  async loadWowzaServers({ commit }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.listEquipmentWowza, {
          filter: {
            equipmentType: { eq: 'WOWZA' }
          },
          limit: 1000
        })
      );

      const servers = result.data.listEquipments.items || [];
      commit('setWowzaServers', servers);

      return { success: true, data: servers };
    } catch (error) {
      console.error('Failed to load Wowza servers:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to load Wowza servers' });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async loadWowzaApplications({ commit }, serverId) {
    commit('setLoading', true);
    commit('clearError');

    try {
      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.listWowzaApplications, {
          serverId
        })
      );

      const response = result.data.listWowzaApplications;

      if (!response.success) {
        throw new Error(response.message || 'Failed to load applications');
      }

      const parsedData = parseCommandDataJson(response.dataJson, {});
      const applications = parsedData.applications || parsedData.data?.applications || [];
      commit('setWowzaApplications', applications);

      return { success: true, data: applications };
    } catch (error) {
      console.error('Failed to load Wowza applications:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to load Wowza applications' });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async loadWowzaTargetsAndTemplates({ commit }, { serverId, applicationId }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      const [targetsResult, templatesResult] = await Promise.all([
        client.graphql(
          graphqlOperation(streamRouterMutations.listWowzaAppTargets, {
            serverId,
            applicationId
          })
        ),
        client.graphql(
          graphqlOperation(streamRouterMutations.listTranscoderTemplates, {
            serverId,
            applicationId
          })
        )
      ]);

      let targets = [];
      let templates = [];

      if (targetsResult.data.listWowzaAppTargets.success) {
        const parsedTargets = parseCommandDataJson(targetsResult.data.listWowzaAppTargets.dataJson, {});
        targets = parsedTargets.targets || parsedTargets.data?.targets || [];
        commit('setWowzaTargets', targets);
      }

      if (templatesResult.data.listTranscoderTemplates.success) {
        const parsedTemplates = parseCommandDataJson(templatesResult.data.listTranscoderTemplates.dataJson, {});
        templates = parsedTemplates.templates || parsedTemplates.data?.templates || [];
        commit('setTranscoderTemplates', templates);
      }

      return { success: true, data: { targets, templates } };
    } catch (error) {
      console.error('Failed to load Wowza targets/templates:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to load Wowza targets or templates' });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async loadMediaConnectNetworkInterfaces({ commit }) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerQueries.listMediaConnectNetworkInterfaces)
      );
      const response = result.data?.listMediaConnectNetworkInterfaces;
      let interfaces = [];

      if (response?.success && response?.dataJson) {
        const parsed = parseCommandDataJson(response.dataJson, {});
        const rawItems = parsed.networkInterfaces || parsed || [];
        interfaces = rawItems.map((item) => ({
          name: `${item.Name} (${item.RegionName})`,
          networkInterfaceArn: item.Arn,
          regionName: item.RegionName,
        }));
      }

      commit('setMediaConnectNetworkInterfaces', interfaces);
      return { success: true, data: interfaces, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectNetworkInterfaces', []);
      commit('setMediaConnectError', error.message || 'Failed to load MediaConnect interfaces');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to load MediaConnect interfaces',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async loadMediaConnectOutputs({ commit }) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerQueries.listMediaConnectOutputs)
      );
      const response = result.data?.listMediaConnectOutputs;
      let outputs = [];

      if (response?.success && response?.dataJson) {
        const parsed = parseCommandDataJson(response.dataJson, {});
        outputs = parsed.outputs || parsed || [];
      }

      commit('setMediaConnectOutputs', outputs);
      return { success: true, data: outputs, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectOutputs', []);
      commit('setMediaConnectError', error.message || 'Failed to load MediaConnect outputs');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to load MediaConnect outputs',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async getMediaConnectOutputById({ commit }, outputId) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerQueries.getMediaConnectOutput, { outputId })
      );
      const response = result.data?.getMediaConnectOutput;
      let output = null;

      if (response?.success && response?.dataJson) {
        const parsed = parseCommandDataJson(response.dataJson, {});
        output = parsed.output || parsed || null;
      }

      return { success: true, data: output, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectError', error.message || 'Failed to load MediaConnect output');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to load MediaConnect output',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async createMediaConnectInput({ commit }, config) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerMutations.createMediaConnectInput, {
          config: JSON.stringify(config),
        })
      );
      const response = result.data?.createMediaConnectInput;

      if (!response?.success) {
        return {
          success: false,
          error: response?.message || 'Unknown error',
          message: response?.message || 'Unknown error',
        };
      }

      const parsed = parseCommandDataJson(response.dataJson, {}) || {};
      return { success: true, data: parsed, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectError', error.message || 'Failed to create MediaConnect input');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to create MediaConnect input',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async createMediaConnectOutput({ commit, dispatch }, config) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerMutations.createMediaConnectOutput, {
          config: JSON.stringify(config),
        })
      );
      const response = result.data?.createMediaConnectOutput;

      if (!response?.success) {
        return {
          success: false,
          error: response?.message || 'Unknown error',
          message: response?.message || 'Unknown error',
        };
      }

      const parsed = parseCommandDataJson(response.dataJson, {}) || {};
      await dispatch('loadMediaConnectOutputs');
      return { success: true, data: parsed, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectError', error.message || 'Failed to create MediaConnect output');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to create MediaConnect output',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async updateMediaConnectOutput({ commit, dispatch }, config) {
    commit('setMediaConnectLoading', true);
    commit('clearMediaConnectError');
    try {
      const result = await client.graphql(
        graphqlOperation(routerMutations.updateMediaConnectOutput, {
          config: JSON.stringify(config),
        })
      );
      const response = result.data?.updateMediaConnectOutput;

      if (!response?.success) {
        return {
          success: false,
          error: response?.message || 'Unknown error',
          message: response?.message || 'Unknown error',
        };
      }

      const parsed = parseCommandDataJson(response.dataJson, {}) || {};
      await dispatch('loadMediaConnectOutputs');
      return { success: true, data: parsed, message: response?.message || '' };
    } catch (error) {
      commit('setMediaConnectError', error.message || 'Failed to update MediaConnect output');
      return {
        success: false,
        error: error.message || String(error),
        message: 'Failed to update MediaConnect output',
      };
    } finally {
      commit('setMediaConnectLoading', false);
    }
  },

  async createExternalWowzaTarget({ commit }, { serverId, applicationId, targetConfig }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      // Validate minimal required fields based on profile
      const config = {
        entryName: targetConfig.entryName || `${new Date().toISOString()}_target`,
        enabled: targetConfig.enabled !== undefined ? targetConfig.enabled : true,
        sourceStreamName: targetConfig.sourceStreamName,
        streamName: targetConfig.streamName,
        host: targetConfig.host,
        profile: targetConfig.profile
      };

      if (!config.sourceStreamName || !config.streamName || !config.host || !config.profile) {
        throw new Error('sourceStreamName, streamName, host, and profile are required');
      }

      // Map profile-specific logic
      if (['rtmp', 'youtube'].includes(config.profile)) {
        config.application = targetConfig.application;
        config.port = targetConfig.port || 1935;
      } else if (config.profile === 'mpegts') {
        config.port = targetConfig.port;
        config.timeToLive = targetConfig.timeToLive !== undefined ? targetConfig.timeToLive : 63;
      } else if (config.profile === 'rtp') {
        config.videoPort = targetConfig.videoPort;
        config.audioPort = targetConfig.audioPort;
        config.timeToLive = targetConfig.timeToLive !== undefined ? targetConfig.timeToLive : 63;
        config.streamWaitTimeout = targetConfig.streamWaitTimeout !== undefined ? targetConfig.streamWaitTimeout : 5000;
      } else if (config.profile === 'srt') {
        config.port = targetConfig.port;
        config.srtLatency = targetConfig.srtLatency !== undefined ? targetConfig.srtLatency : 400;
        config.srtTooLatePacketDrop = targetConfig.srtTooLatePacketDrop !== undefined ? targetConfig.srtTooLatePacketDrop : true;
        config.srtTimestampBasedDeliveryMode = targetConfig.srtTimestampBasedDeliveryMode !== undefined ? targetConfig.srtTimestampBasedDeliveryMode : true;
        config.srtSendBufferSize = targetConfig.srtSendBufferSize !== undefined ? targetConfig.srtSendBufferSize : 12058624;
        config.srtSendBufferSizeUDP = targetConfig.srtSendBufferSizeUDP !== undefined ? targetConfig.srtSendBufferSizeUDP : 65536;
        config.srtMaximumSegmentSize = targetConfig.srtMaximumSegmentSize !== undefined ? targetConfig.srtMaximumSegmentSize : 1500;
        config.srtFlightFlagSize = targetConfig.srtFlightFlagSize !== undefined ? targetConfig.srtFlightFlagSize : 25600;
        config.srtMaximumBandwidth = targetConfig.srtMaximumBandwidth !== undefined ? targetConfig.srtMaximumBandwidth : -1;
        config.srtInputBandwidth = targetConfig.srtInputBandwidth !== undefined ? targetConfig.srtInputBandwidth : 0;
        config.srtOverheadBandwidth = targetConfig.srtOverheadBandwidth !== undefined ? targetConfig.srtOverheadBandwidth : 25;
        config.srtConnectTimeout = targetConfig.srtConnectTimeout !== undefined ? targetConfig.srtConnectTimeout : 3000;
        config.srtPeerIdleTimeout = targetConfig.srtPeerIdleTimeout !== undefined ? targetConfig.srtPeerIdleTimeout : 5000;
        config.srtStreamId = targetConfig.srtStreamId !== undefined ? targetConfig.srtStreamId : '';
      }

      console.log('Creating Wowza target with profile config:', config);

      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.createExternalWowzaTarget, {
          serverId,
          applicationId,
          targetConfig: config
        })
      );

      const response = result.data.createExternalWowzaTarget;

      if (!response.success) {
        throw new Error(response.message || 'Failed to create target');
      }

      commit('addWowzaTarget', {
        serverId,
        applicationId,
        ...response.data
      });

      this._vm?.$message?.success({ message: 'Wowza target created successfully' });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to create Wowza target:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to create Wowza target: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async updateExternalWowzaTarget({ commit }, { serverId, applicationId, targetId, targetConfig }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      // Re-use same payload formatting structure as create
      const config = {
        entryName: targetConfig.entryName,
        enabled: targetConfig.enabled !== undefined ? targetConfig.enabled : true,
        sourceStreamName: targetConfig.sourceStreamName,
        streamName: targetConfig.streamName,
        host: targetConfig.host,
        profile: targetConfig.profile
      };

      if (!config.sourceStreamName || !config.streamName || !config.host || !config.profile) {
        throw new Error('sourceStreamName, streamName, host, and profile are required');
      }

      if (['rtmp', 'youtube'].includes(config.profile)) {
        config.application = targetConfig.application;
        config.port = targetConfig.port || 1935;
      } else if (config.profile === 'mpegts') {
        config.port = targetConfig.port;
        config.timeToLive = targetConfig.timeToLive !== undefined ? targetConfig.timeToLive : 63;
      } else if (config.profile === 'rtp') {
        config.videoPort = targetConfig.videoPort;
        config.audioPort = targetConfig.audioPort;
        config.timeToLive = targetConfig.timeToLive !== undefined ? targetConfig.timeToLive : 63;
        config.streamWaitTimeout = targetConfig.streamWaitTimeout !== undefined ? targetConfig.streamWaitTimeout : 5000;
      } else if (config.profile === 'srt') {
        config.port = targetConfig.port;
        config.srtLatency = targetConfig.srtLatency !== undefined ? targetConfig.srtLatency : 400;
        config.srtTooLatePacketDrop = targetConfig.srtTooLatePacketDrop !== undefined ? targetConfig.srtTooLatePacketDrop : true;
        config.srtTimestampBasedDeliveryMode = targetConfig.srtTimestampBasedDeliveryMode !== undefined ? targetConfig.srtTimestampBasedDeliveryMode : true;
        config.srtSendBufferSize = targetConfig.srtSendBufferSize !== undefined ? targetConfig.srtSendBufferSize : 12058624;
        config.srtSendBufferSizeUDP = targetConfig.srtSendBufferSizeUDP !== undefined ? targetConfig.srtSendBufferSizeUDP : 65536;
        config.srtMaximumSegmentSize = targetConfig.srtMaximumSegmentSize !== undefined ? targetConfig.srtMaximumSegmentSize : 1500;
        config.srtFlightFlagSize = targetConfig.srtFlightFlagSize !== undefined ? targetConfig.srtFlightFlagSize : 25600;
        config.srtMaximumBandwidth = targetConfig.srtMaximumBandwidth !== undefined ? targetConfig.srtMaximumBandwidth : -1;
        config.srtInputBandwidth = targetConfig.srtInputBandwidth !== undefined ? targetConfig.srtInputBandwidth : 0;
        config.srtOverheadBandwidth = targetConfig.srtOverheadBandwidth !== undefined ? targetConfig.srtOverheadBandwidth : 25;
        config.srtConnectTimeout = targetConfig.srtConnectTimeout !== undefined ? targetConfig.srtConnectTimeout : 3000;
        config.srtPeerIdleTimeout = targetConfig.srtPeerIdleTimeout !== undefined ? targetConfig.srtPeerIdleTimeout : 5000;
        config.srtStreamId = targetConfig.srtStreamId !== undefined ? targetConfig.srtStreamId : '';
      }

      console.log('Updating Wowza target with profile config:', config);

      const result = await client.graphql(
        graphqlOperation(streamRouterMutations.updateExternalWowzaTarget, {
          serverId,
          applicationId,
          targetId,
          targetConfig: config
        })
      );

      const response = result.data.updateExternalWowzaTarget;

      if (!response.success) {
        throw new Error(response.message || 'Failed to update target');
      }

      this._vm?.$message?.success({ message: 'Wowza target updated successfully' });

      // We don't necessarily update local Wowza items immediately depending on how the frontend handles reload, 
      // but returning true triggers external handling usually.
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to update Wowza target:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to update Wowza target: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  // Output/Input Creation with integrated logic
  async createROUTEOutput({ dispatch, commit }, { outputData, routeConfig }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      // 1. Create Stream Router Interface with destination.url
      const interfaceResult = await dispatch('createStreamRouterInterface', {
        streamRouterId: routeConfig.streamRouterId,
        interfaceData: {
          name: routeConfig.interfaceName,
          source: {
            url: routeConfig.sourceUrl,
            type: 'srt',
            max_connections: 1,
            latency: routeConfig.latency || 2000
          },
          destination: {
            url: routeConfig.selectedDestination.url, // Critical: Use selected destination URL
            type: 'srt',
            max_connections: 1,
            latency: routeConfig.latency || 2000
          },
          processors: routeConfig.processors || [],
          tags: routeConfig.tags || [],
          whitelist: routeConfig.whitelist || [],
          autostart: routeConfig.autostart || true
        }
      });

      if (!interfaceResult.success) {
        throw new Error('Failed to create Stream Router Interface: ' + interfaceResult.error);
      }

      // 2. Create Output with SR data
      const outputInput = {
        ...outputData,
        outputType: 'ROUTE',
        smEndpoints: JSON.stringify(interfaceResult.smEndpoints),
        config: JSON.stringify(interfaceResult.config)
      };

      const outputResult = await client.graphql(
        graphqlOperation(qlmutations.createOutput, {
          input: outputInput
        })
      );

      this._vm?.$message?.success({ message: 'ROUTE Output created successfully' });

      return {
        success: true,
        data: {
          output: outputResult.data.createOutput,
          interface: interfaceResult.data
        }
      };
    } catch (error) {
      console.error('Failed to create ROUTE Output:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to create ROUTE Output: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async createOTTOutput({ dispatch, commit }, { outputData, ottConfig, createNewTarget = false, newTargetConfig = null }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      let targetData = ottConfig.selectedTarget;

      // 1. Create new target if requested
      if (createNewTarget && newTargetConfig) {
        const targetResult = await dispatch('createExternalWowzaTarget', {
          serverId: ottConfig.ottServer,
          applicationId: ottConfig.ottApplication,
          targetConfig: newTargetConfig
        });

        if (!targetResult.success) {
          throw new Error('Failed to create new target: ' + targetResult.error);
        }

        targetData = targetResult.data;
      }

      // 2. Create Output with OTT configuration
      const config = {
        server: ottConfig.ottServer,
        application: ottConfig.ottApplication,
        target: targetData.id || targetData.name,
        platform: ottConfig.ottPlatform,
        defaultTranscoderProfile: ottConfig.ottDefaultTranscoderProfile,
        streamKeyRequired: !ottConfig.ottNoStremKeyRequired
      };

      const outputInput = {
        ...outputData,
        name: targetData.name, // Name MUST be identical to target name
        outputType: 'OTT',
        ottPlatform: ottConfig.ottPlatform,
        ottServer: ottConfig.ottServer,
        ottApplication: ottConfig.ottApplication,
        ottNoStremKeyRequired: ottConfig.ottNoStremKeyRequired,
        ottDefaultTranscoderProfile: ottConfig.ottDefaultTranscoderProfile,
        config: JSON.stringify(config)
      };

      const outputResult = await client.graphql(
        graphqlOperation(qlmutations.createOutput, {
          input: outputInput
        })
      );

      this._vm?.$message?.success({ message: 'OTT Output created successfully' });

      return {
        success: true,
        data: {
          output: outputResult.data.createOutput,
          target: targetData,
          config
        }
      };
    } catch (error) {
      console.error('Failed to create OTT Output:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to create OTT Output: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  async createInput({ dispatch, commit }, { inputData, selectedDestination, streamRouterId }) {
    commit('setLoading', true);
    commit('clearError');

    try {
      // Create Stream Router Interface for Input with destination.url
      const interfaceResult = await dispatch('createStreamRouterInterface', {
        streamRouterId: streamRouterId,
        interfaceData: {
          name: inputData.name,
          source: {
            url: inputData.sourceUrl || 'srt://input:port',
            type: 'srt'
          },
          destination: {
            url: selectedDestination.url, // Critical: Use selected destination URL
            type: 'srt'
          },
          processors: [],
          tags: [],
          whitelist: [],
          autostart: true
        }
      });

      if (!interfaceResult.success) {
        throw new Error('Failed to create interface for Input: ' + interfaceResult.error);
      }

      // Create Input record
      const inputInput = {
        ...inputData,
        // Store interface reference if needed
        config: JSON.stringify({
          interfaceId: interfaceResult.data.interfaceId,
          streamRouterId: streamRouterId,
          type: 'SR_INPUT'
        })
      };

      const inputResult = await client.graphql(
        graphqlOperation(qlmutations.createInput, {
          input: inputInput
        })
      );

      this._vm?.$message?.success({ message: 'Input created successfully' });

      return {
        success: true,
        data: {
          input: inputResult.data.createInput,
          interface: interfaceResult.data
        }
      };
    } catch (error) {
      console.error('Failed to create Input:', error);
      commit('setError', error.message);
      this._vm?.$message?.error({ message: 'Failed to create Input: ' + error.message });
      return { success: false, error: error.message };
    } finally {
      commit('setLoading', false);
    }
  },

  // Cleanup utilities
  async cleanupInterfaces({ dispatch }, interfaces) {
    const results = [];

    for (const iface of interfaces) {
      const result = await dispatch('deleteStreamRouterInterface', {
        streamRouterId: iface.streamRouterId,
        interfaceId: iface.interfaceId
      });
      results.push({ interface: iface, result });
    }

    return results;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
